import { TablePagination } from '@mui/material'
import React, { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import styles from './escalatedPage.module.css';
import { UserContext, UserPayload } from '../../Context/userContext';
import EscalatedTable from '../../Components/escalatedTable/escalatedTable';
import { rowCountPerPageOptions } from '../../constants/rowsPerPageOptions';
import { EscalationFilters, IEscalatedPageDetailModel } from '../../Models/escalatedPageModels';
import { useEscalationInfo } from '../../Context/escalationContext';
import { useInsights } from '../../Context/insightContext';
import { EscalatePageHeader } from './components/escalatePageHeader/escalatePageHeader.index';
import { buildPayload } from './components/escalatePageHeader/escalatedPageHeader.utils';
import { useEscalatedDataFetch } from './escalatedPage.hooks';
import { DEFAULT_ESCALATION_STATUS_COUNT, ESCALATION_TBL_DEFAULT_COLS, HIDDEN_COMBINED_NAME_KEYS, PAGINATION } from './escalatedPage.constants';
import { getDefaultEscalationFilters, normalizeEscalationOppIdQuery } from './escalatedPage.utils';
import { ISortObj } from '../../Models/applicationModels';
import { getEscalationColumnLabel } from './escalatedPage.table.utils';
import { APPLICATION_ESCALATION_UID_KEY } from '../../Components/CreateEscalation/createEscalation.constants';

const APPLICATION_ESCALATION_KEY = APPLICATION_ESCALATION_UID_KEY.toLocaleLowerCase();

const EscalatedPage = () => {
    const [headerNames, setHeaderNames] = useState<string[]>([])
    const [visibleColumns, setVisibleColumns] = useState<string[] | null>(null);

    const { userDetail } = React.useContext(UserContext) as UserPayload;
    const {
        escalatedData,
        paginationDetail,
        setPaginationDetail,
        setEscalationStatusesCount,
        setTotalEscalationsCount,
        totalEscalationsCount,
        searchValue,
        sortInfo,
        setSortInfo,
        setSearchValue,
    } = useEscalationInfo();
    const [searchParams, setSearchParams] = useSearchParams();
    const { setEscalationDialogInfo } = useInsights();

    const { fetchEscalatedData, fetchEscalationStatusList, fetchCreatedByList, fetchEscalationList } = useEscalatedDataFetch();

    const [appliedFilters, setAppliedFilters] = React.useState<EscalationFilters>(getDefaultEscalationFilters(userDetail.value.devId));

    useEffect(() => {
        if (!escalatedData || escalatedData?.length === 0) {
            return
        }
        const formattedKeys = Object.keys(escalatedData[0])
            .filter((key) => !HIDDEN_COMBINED_NAME_KEYS.has(key as keyof IEscalatedPageDetailModel))
            .map((key) => getEscalationColumnLabel(key as keyof IEscalatedPageDetailModel));
        setHeaderNames(formattedKeys)
    }, [escalatedData])

    useEffect(() => {
        (async () => {
            const newParams = new URLSearchParams([...searchParams].map(([key, value]) => [key.toLowerCase(), value]));
            const hadApplicationEscUidInUrl = newParams.has(APPLICATION_ESCALATION_KEY);
            const applicationEscUid = normalizeEscalationOppIdQuery(newParams.get(APPLICATION_ESCALATION_KEY));
            if (applicationEscUid) {
                setSearchValue(applicationEscUid);
            }
            const payload = buildPayload({
                filters: appliedFilters,
                pageSize: PAGINATION.DEFAULT_ROWS_PER_PAGE,
                pageIndex: PAGINATION.DEFAULT_PAGE,
                searchParam: applicationEscUid,
                sort: sortInfo,
            });

            await Promise.all([
                fetchEscalationList(),
                fetchEscalationStatusList(),
                fetchCreatedByList(),
                fetchEscalatedData(payload)
            ]);

            if (hadApplicationEscUidInUrl) {
                setSearchParams(
                    (prev) => {
                        const next = new URLSearchParams(prev);
                        for (const key of Array.from(next.keys())) {
                            if (key.toLowerCase() === APPLICATION_ESCALATION_KEY) {
                                next.delete(key);
                            }
                        }
                        return next;
                    },
                    { replace: true }
                );
            }
        })();
    }, []);

    const getDataOnPaginationChange = async (pageNumber: number, rowPerPageCount: number) => {
        const payload = buildPayload({
            filters: appliedFilters,
            pageSize: rowPerPageCount,
            pageIndex: pageNumber + 1,
            searchParam: searchValue,
            sort: sortInfo
        });
        const response = await fetchEscalatedData(payload);
        if (response) {
            setTotalEscalationsCount(response.totalEscalations);
            setEscalationStatusesCount(response.escalationStatusesCount ?? DEFAULT_ESCALATION_STATUS_COUNT);
        }
    };

    const handleEscalationDialogClose = React.useCallback(
        async (flagUpdateData: boolean) => {
            if (flagUpdateData) {
                const payload = buildPayload({
                    filters: appliedFilters,
                    pageSize: paginationDetail.rowsPerPage,
                    pageIndex: paginationDetail.page + 1,
                    searchParam: searchValue,
                    sort: sortInfo
                });
                await fetchEscalatedData(payload);
            }
            setEscalationDialogInfo(null);  
        },
        [appliedFilters, paginationDetail.rowsPerPage, paginationDetail.page, searchValue, fetchEscalatedData, setEscalationDialogInfo]
    );

    const onSortChange = useCallback(async (sortItem: ISortObj | null) => {
        const payload = buildPayload({
            filters: appliedFilters,
            pageSize: paginationDetail.rowsPerPage,
            pageIndex: paginationDetail.page + 1,
            searchParam: searchValue,
            sort: sortItem
        });
        const res = await fetchEscalatedData(payload);
        if (res) {
            setSortInfo(sortItem);
        }
    }, [appliedFilters, paginationDetail.rowsPerPage, paginationDetail.page, searchValue, fetchEscalatedData, setSortInfo]);

    return (
        <div className={styles.headerCtn}>
            <EscalatePageHeader headerNames={headerNames} fixedColumns={ESCALATION_TBL_DEFAULT_COLS} visibleColumns={visibleColumns} setVisibleColumns={setVisibleColumns} appliedFilters={appliedFilters} setAppliedFilters={setAppliedFilters} />
            <div className={styles.tableSection}>
                <EscalatedTable
                    headerNames={headerNames}
                    visibleColumns={visibleColumns ?? []}
                    handleCloseEscalationPopUp={handleEscalationDialogClose}
                    onSortChange={onSortChange}
                />
            </div>
            {escalatedData && escalatedData?.length > 0 && (
                <div className={styles.tablePaginationCtn}>
                    <TablePagination
                        rowsPerPageOptions={rowCountPerPageOptions}
                        component="div"
                        count={totalEscalationsCount}
                        rowsPerPage={paginationDetail.rowsPerPage ?? PAGINATION.DEFAULT_ROWS_PER_PAGE}
                        page={paginationDetail.page ?? 0}
                        onPageChange={(event, newPage) => {
                            getDataOnPaginationChange(newPage, paginationDetail.rowsPerPage)
                            setPaginationDetail((prevState) => ({
                                ...prevState,
                                page: newPage
                            }));
                        }}
                        onRowsPerPageChange={(event) => {
                            getDataOnPaginationChange(0, Number(event.target.value))
                            setPaginationDetail((prevState) => ({
                                ...prevState,
                                page: 0,
                                rowsPerPage: Number(event.target.value)
                            }));
                        }}
                    />
                </div>
            )}
        </div>
    )
}

export default EscalatedPage